import { computed } from 'vue'
import { useThemeStore } from '@uiKit/store/themeStore'

/**
 * Resolves `--p-*` theme tokens to concrete color strings.
 *
 * Canvas APIs (Chart.js gradients, tooltip chrome) can't read CSS custom
 * properties — they need a resolved value. Reading it here keeps chart colors
 * on the theme tokens instead of hardcoded hex, and `isDark` gives callers a
 * reactive dependency so their computed palette re-resolves when the theme
 * class on `<html>` flips.
 *
 * ```ts
 * const { isDark, readToken } = useThemeTokens()
 * const palette = computed(() => ({
 *   line: readToken('--p-primary-400', '#7e79ff'),
 *   axis: readToken(isDark.value ? '--p-surface-500' : '--p-surface-400', '#a4a7ae'),
 * }))
 * ```
 */
export function useThemeTokens() {
  const themeStore = useThemeStore()

  const isDark = computed(() => themeStore.isDarkMode)

  /** Reads a custom property off `<html>`, falling back when it isn't set. */
  function readToken(name: string, fallback: string): string {
    if (typeof document === 'undefined') return fallback
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
  }

  return { isDark, readToken }
}
